/**
 * MockAgentBuilderService — localStorage-backed implementation of
 * IAgentBuilderService for Phase 1 demos. Replaced by RealAgentBuilderService
 * in Phase C0 via the AGENT_BUILDER_SERVICE token.
 *
 * Phase 1 gap resolutions integrated:
 *   - P1-G5  LRU preview cache (see ./preview-cache.ts)
 *   - P1-G10 ISO timestamps (unix-seconds; RelativeTimePipe handles display)
 *   - P1-G11 Slow AI mode for AI-assist (via scenario-hints)
 *   - P1-G12 partial_merge state in publish cascade
 *   - P1-G13 ?retry=succeed|fail outcome
 *   - P1-G16 storage event cross-tab sync
 *   - P1-G17 optimistic locking via version + ConflictError
 *   - P1-G19 ?smoke=ok|timeout|tool-fail outcome
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, defer, of, throwError, timer } from 'rxjs';
import { catchError, delay, map, mergeMap, takeUntil } from 'rxjs/operators';
// Browser-native UUID (replaces the uuid package).
const uuid = (): string => crypto.randomUUID();

import { environment } from '../../../environments/environment';
import { CANNED_AI_PROMPTS, SEED_DRAFTS, SEED_PUBLISHED, STATIC_IAM_TEMPLATE } from '../mock-data/seed';
import { readScenarioHints } from '../mock-data/scenario-hints';
import { pickToolForMessage } from '../utils/tool-match';
import { coBuilderLocalParse } from '../utils/cobuilder-local';
import { deriveReportFromDescription, reportSkeletonMarkdown } from '../utils/report-derive';
import { defaultEmailBodyMarkdown } from '../utils/email-doc';
import { mockFreshReportUrl } from '../utils/report-url';
import { getPreview, invalidatePreview, putPreview } from '../mock-data/preview-cache';
import { ABILITY_CATEGORIES } from '../data/ability-catalog';
import { REPORT_TEMPLATE_PRESETS } from '../data/report-template-presets';
import {
  AbilityRequest,
  AgentRun,
  AgentRunsResult,
  AnthropicToolSchema,
  CoBuilderChatMessage,
  CoBuilderDocument,
  CoBuilderResult,
  CoBuilderStreamEvent,
  CoBuilderTurn,
  ComingSoonAbility,
  ConflictError,
  DirectoryAgent,
  Draft,
  DraftCodegen,
  DraftState,
  DraftTestChatResult,
  DraftTestRun,
  DataCandidate,
  EmailPreview,
  GatewayTarget,
  GroupOption,
  IAgentBuilderService,
  KnowledgeDocStatus,
  KnowledgeDocument,
  KnowledgeDocumentList,
  KnowledgeQuota,
  NotFoundError,
  PublishedAgent,
  TransferOwnerResult,
  TransferRef,
  TransferCheck,
  TransferEffect,
  OwnerHistory,
  OwnerHistoryEntry,
  PublishResult,
  ReportTemplatePreset,
  ReportTemplateResult,
  EmailTemplateResult,
  ScheduleState,
  SmokeTestResult,
  TestChatStreamEvent,
  SubmissionCheck,
  ToolCatalogResult,
  ValidationResult,
  WizardInput,
  WizardPrompt,
  WizardPerRecipient,
} from './agent-builder.interface';

const STORAGE_DRAFTS = 'studio-mock-drafts';
const STORAGE_PUBLISHED = 'studio-mock-published';
const STORAGE_SEEDED = 'studio-mock-seeded-v1';
const STORAGE_RUNS_PREFIX = 'studio-mock-runs:';
const STORAGE_SCHEDULE_PREFIX = 'studio-mock-schedule:';
const STORAGE_KNOWLEDGE_PREFIX = 'studio-mock-knowledge:';

/** Mock per-draft knowledge-base budget (mirrors the server quota shape). */
const MOCK_KNOWLEDGE_MAX_DOCS = 100;
const MOCK_KNOWLEDGE_MAX_TOTAL_BYTES = 100 * 1024 * 1024; // 100 MB
const MOCK_KNOWLEDGE_MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB

const DELAY_FAST_MS = 350;
const DELAY_AI_FAST_MS = 800;
const DELAY_AI_SLOW_MS = 12_000;

const STORAGE_ABILITY_REQUESTS = 'studio-mock-ability-requests';

/** Curated roadmap mirror of the studio-api coming-soon list, so the Abilities
 *  library's "Coming soon" + "Request a connector" flows demo offline. */
const MOCK_COMING_SOON: ComingSoonAbility[] = [
  { id: 'soon_bamboo', name: 'Read HR / people data', system: 'BambooHR', desc: 'Look up employee records, org chart and time off.', badge: 'Reads BambooHR', category: 'Knowledge', icon: 'users', eta: 'In review', status: 'coming_soon' },
  { id: 'soon_slack', name: 'Post to Slack', system: 'Slack', desc: 'Share a summary into a Slack channel your team follows.', badge: 'Posts messages', category: 'Communication', icon: 'chat', eta: 'In review', status: 'coming_soon' },
  { id: 'soon_jira_write', name: 'Update engineering tickets', system: 'Jira', desc: 'Create and update Jira issues, not just read them.', badge: 'Writes to Jira', category: 'Support', icon: 'alert', eta: 'Next quarter', status: 'coming_soon' },
  { id: 'soon_powerbi', name: 'Read dashboards', system: 'Power BI', desc: 'Pull figures from published Power BI reports.', badge: 'Reads Power BI', category: 'Documents', icon: 'chart', eta: 'Planned', status: 'coming_soon' },
  { id: 'soon_servicenow', name: 'Read & update IT tickets', system: 'ServiceNow', desc: 'Look up and update ServiceNow incidents and requests.', badge: 'Reads/writes ServiceNow', category: 'Support', icon: 'wrench', eta: 'Planned', status: 'coming_soon' },
];

/** Wire names per target in the canned catalog below — used by the mock's
 *  submission checks to mirror the server's approved-catalog test. */
const MOCK_CATALOG_OPS: Array<{ target: string; op: string }> = [
  { target: 'atlassian-read', op: 'search_jira_issues' },
  { target: 'atlassian-read', op: 'search_confluence' },
  { target: 'salesforce-read', op: 'query_accounts' },
  { target: 'salesforce-read', op: 'query_opportunities' },
  { target: 'salesforce-read', op: 'query_cases' },
  { target: 'snowflake-read', op: 'query_partner_revenue' },
  { target: 'snowflake-read', op: 'list_wins' },
  { target: 'chilipiper-read', op: 'cp_get_availability' },
  { target: 'chilipiper-write', op: 'cp_book_meeting' },
];

@Injectable({ providedIn: 'root' })
export class MockAgentBuilderService implements IAgentBuilderService {
  private readonly http = inject(HttpClient);
  /** Emits the changed storage key when localStorage changes in another tab. */
  private readonly externalChange$ = new Subject<string>();

  constructor() {
    this.seedIfNeeded();
    window.addEventListener('storage', this.onStorage);
  }

  // ── localStorage helpers ─────────────────────────────────────────────

  private now(): number {
    return Math.floor(Date.now() / 1000);
  }

  private currentUser(): string {
    return sessionStorage.getItem('current-username') || 'demo@watchguard.com';
  }

  private currentRole(): string {
    return sessionStorage.getItem('current-role') || 'developer';
  }

  private readDrafts(): Draft[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_DRAFTS) || '[]') as Draft[];
    } catch {
      return [];
    }
  }

  private writeDrafts(drafts: Draft[]): void {
    try {
      localStorage.setItem(STORAGE_DRAFTS, JSON.stringify(drafts));
    } catch {
      /* P1-G5: quota handled in preview-cache; drafts themselves are small. */
    }
  }

  private readPublished(): PublishedAgent[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_PUBLISHED) || '[]') as PublishedAgent[];
    } catch {
      return [];
    }
  }

  private writePublished(agents: PublishedAgent[]): void {
    try {
      localStorage.setItem(STORAGE_PUBLISHED, JSON.stringify(agents));
    } catch {
      /* ignore */
    }
  }

  private seedIfNeeded(): void {
    // In real mode this service is only the W5/W6 fallback inside
    // RealAgentBuilderService — never seed demo agents there.
    if (!environment.useMockData) return;
    if (localStorage.getItem(STORAGE_SEEDED)) return;
    if (this.readDrafts().length === 0) this.writeDrafts(SEED_DRAFTS);
    if (this.readPublished().length === 0) this.writePublished(SEED_PUBLISHED);
    localStorage.setItem(STORAGE_SEEDED, '1');
  }

  /** P1-G16 cross-tab sync. */
  private readonly onStorage = (e: StorageEvent): void => {
    if (!e.key || !e.key.startsWith('studio-mock-')) return;
    this.externalChange$.next(e.key);
  };

  /** Simulated network latency. */
  private withDelay<T>(value: T, ms = DELAY_FAST_MS): Observable<T> {
    return of(value).pipe(delay(ms));
  }

  // ── Drafts ──────────────────────────────────────────────────────────

  listDrafts(): Observable<Draft[]> {
    const me = this.currentUser();
    return this.withDelay(this.readDrafts().filter((d) => d.created_by === me));
  }

  getDraft(id: string): Observable<Draft> {
    const draft = this.readDrafts().find((d) => d.id === id);
    if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));
    return this.withDelay(draft);
  }

  createDraft(initial?: Partial<WizardInput>): Observable<Draft> {
    const me = this.currentUser();
    const now = this.now();
    const defaults: WizardInput = {
      identity: {
        agent_id: '',
        agent_dir: '',
        display_name: '',
        description: '',
        // ROI mandate: both start unset — publish is blocked until the
        // creator picks them (mirrors studio-api _default_inputs).
        department: '',
        keywords: [],
        time_saved_minutes_per_session: null,
        time_saved_basis: '',
      },
      shape: {
        has_tools: false,
        tools_via_gateway: false,
        gateway_target: null,
        has_report_artifact: false,
        has_rolling_history: true,
        has_rate_limit: true,
        custom_actions: [],
      },
      prompt: { role: '', instructions: '' },
      tools: [],
      iam_overrides: { extra_statements: [] },
      meta: { renderer: null, capabilities: [], required_inputs: [], suggested_actions: [] },
    };
    const draft: Draft = {
      id: uuid(),
      version: 1,
      state: 'draft',
      created_by: me,
      created_at: now,
      last_modified: now,
      inputs: { ...defaults, ...(initial ?? {}) } as WizardInput,
      audit_trail: [{ actor: me, action: 'created', ts: now }],
    };
    const all = this.readDrafts();
    all.push(draft);
    this.writeDrafts(all);
    return this.withDelay(draft);
  }

  updateDraft(id: string, patch: Partial<WizardInput>, expectedVersion: number): Observable<Draft> {
    return defer(() => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) {
        return throwError(() => new NotFoundError(`Draft ${id} not found`));
      }
      const current = all[idx];

      // P1-G17 optimistic locking.
      if (current.version !== expectedVersion) {
        return throwError(
          () => new ConflictError(
            'Draft was updated by another session. Reload to see the latest version.',
            current.version,
            current.state,
          ),
        );
      }

      const updated: Draft = {
        ...current,
        inputs: { ...current.inputs, ...patch },
        version: current.version + 1,
        last_modified: this.now(),
      };
      // Derive agent_dir from agent_id if changed.
      if (patch.identity?.agent_id) {
        updated.inputs.identity.agent_dir = patch.identity.agent_id.toLowerCase();
      }
      all[idx] = updated;
      this.writeDrafts(all);
      invalidatePreview(id); // P1-G5: preview is stale after a patch.
      return this.withDelay(updated);
    });
  }

  deleteDraft(id: string): Observable<void> {
    const all = this.readDrafts();
    const filtered = all.filter((d) => d.id !== id);
    this.writeDrafts(filtered);
    invalidatePreview(id);
    return this.withDelay(undefined as void);
  }

  validateDraft(id: string): Observable<ValidationResult> {
    const draft = this.readDrafts().find((d) => d.id === id);
    if (!draft) {
      return throwError(() => new NotFoundError(`Draft ${id} not found`));
    }

    // P1-G2 mock-side validator: ONLY regex + lockstep + conflict check.
    // Server is authoritative for everything else.
    const errors: ValidationResult['errors'] = [];
    const id_ = draft.inputs.identity.agent_id;
    const STUDIO_REGEX = /^STUDIO_[A-Z0-9_]{0,39}[A-Z0-9]$/;
    if (!STUDIO_REGEX.test(id_)) {
      errors.push({
        field: 'identity.agent_id',
        code: 'regex',
        message: 'Must match ^STUDIO_[A-Z0-9_]{0,39}[A-Z0-9]$ (STUDIO_ prefix; UPPERCASE; max 34 chars; no trailing _).',
      });
    } else if (id_ && id_.length > 34) {
      errors.push({
        field: 'identity.agent_id',
        code: 'too_long',
        message: `Agent ID '${id_}' is too long: the IAM role name would exceed 64 characters. Shorten the agent ID so its lowercase form is at most 34 characters (currently ${id_.length}).`,
      });
    }
    if (id_ && draft.inputs.identity.agent_dir !== id_.toLowerCase()) {
      errors.push({
        field: 'identity.agent_dir',
        code: 'lockstep',
        message: 'agent_dir must equal agent_id.lower()',
      });
    }
    if (id_) {
      const taken = this.readPublished().some((a) => a.agent_id === id_);
      if (taken) {
        errors.push({
          field: 'identity.agent_id',
          code: 'conflict',
          message: `An agent with id ${id_} already exists.`,
        });
      }
    }
    if (!draft.inputs.identity.display_name) {
      errors.push({ field: 'identity.display_name', code: 'required', message: 'Display name is required.' });
    }

    return this.withDelay({ ok: errors.length === 0, errors, checks: this.computeSubmissionChecks(draft) });
  }

  /** Mirror of studio-api submission_checks (mock parity): same labels, same
   *  semantics, computed from the draft + canned catalog. */
  private computeSubmissionChecks(draft: Draft): SubmissionCheck[] {
    const { identity, prompt, shape, tools, iam_overrides } = draft.inputs;
    const ok = (label: string, isOk: boolean, note?: string): SubmissionCheck =>
      note ? { label, ok: isOk, note } : { label, ok: isOk };

    const configOk =
      /^STUDIO_[A-Z0-9_]{0,39}[A-Z0-9]$/.test(identity.agent_id) &&
      !!identity.display_name && (!!prompt.role || !!prompt.instructions);

    // The canned catalog is known statically here; reuse the same wire-name rule.
    const catalogOps = new Set(
      MOCK_CATALOG_OPS.filter((o) => o.target === shape.gateway_target).map((o) => o.op),
    );
    const custom = tools.filter((t) => !catalogOps.has(t.name)).map((t) => t.name || '?');
    const writeTools = shape.gateway_target?.endsWith('-write') ? tools.map((t) => t.name || '?') : [];

    return [
      ok('Configuration is valid', configOk, configOk ? undefined : 'identity or instructions incomplete'),
      !shape.has_tools || tools.length === 0
        ? ok('Tools come from the approved catalog', true, 'no tools — chat only')
        : custom.length > 0
          ? ok('Tools come from the approved catalog', false,
               `${custom.length} custom tool${custom.length === 1 ? '' : 's'} not in the published catalog: ${custom.slice(0, 5).join(', ')}`)
          : ok('Tools come from the approved catalog', true, `${tools.length} tool${tools.length === 1 ? '' : 's'} from ${shape.gateway_target}`),
      writeTools.length > 0
        ? ok('No write-action tools', false, `changes real-world state: ${writeTools.slice(0, 5).join(', ')} — extra scrutiny required`)
        : ok('No write-action tools', true, tools.length ? 'read-only tools' : undefined),
      ok('Fair-use limit configured', shape.has_rate_limit,
         shape.has_rate_limit ? undefined : 'disabled — confirm this is a batch/report agent'),
      ok('No extra IAM permissions', iam_overrides.extra_statements.length === 0,
         iam_overrides.extra_statements.length ? `${iam_overrides.extra_statements.length} extra statement(s) — review scope` : undefined),
      ok('No secrets in the instructions',
         !/AKIA[0-9A-Z]{16}|-----BEGIN [A-Z ]*PRIVATE KEY-----|bearer\s+[a-z0-9_\-.=]{20,}|\b(password|passwd|api[_-]?key|secret[_-]?key)\s*[:=]\s*\S{4,}/i
           .test(`${prompt.role}\n${prompt.instructions}`)),
    ];
  }

  previewDraft(id: string): Observable<Record<string, string>> {
    const cached = getPreview(id);
    if (cached) return this.withDelay(cached, 50);

    const draft = this.readDrafts().find((d) => d.id === id);
    if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));

    const files = this.renderPreview(draft);
    putPreview(id, files);
    return this.withDelay(files, 500);
  }

  private renderPreview(draft: Draft): Record<string, string> {
    const id = draft.inputs.identity.agent_id || 'STUDIO_AGENT';
    const dir = id.toLowerCase();
    const pascal = id
      .split('_')
      .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
      .join('');

    const conditionals: string[] = [];
    if (draft.inputs.shape.tools_via_gateway) {
      conditionals.push(`        - Effect: Allow
          Action: [bedrock-agentcore:InvokeGateway]
          Resource: !Sub "arn:aws:bedrock-agentcore:\${AWS::Region}:\${AWS::AccountId}:gateway/*"`);
    }
    if (draft.inputs.shape.has_report_artifact) {
      conditionals.push(`        - Effect: Allow
          Action: [s3:PutObject, s3:PutObjectTagging]
          Resource: !Sub "arn:aws:s3:::\${WgAgentsReportsBucketName}/*"`);
    }
    const iam = STATIC_IAM_TEMPLATE
      .replace(/{{AGENT_DIR}}/g, dir)
      .replace(/{{PASCAL}}/g, pascal)
      .replace(/{{CONDITIONAL_STATEMENTS}}/g, conditionals.join('\n'));

    const config = `agent_name: ${id}
agent_id: ${id}
description: ${draft.inputs.identity.description}
region: us-west-2
deployment_type: direct_code_deploy
runtime: PYTHON_3_13
entrypoint: app.py
log_level: INFO
memory_enabled: false
${draft.inputs.shape.tools_via_gateway ? 'gateway_required: true\n' : ''}`;

    const meta = JSON.stringify(
      {
        agentId: id,
        displayName: draft.inputs.identity.display_name,
        description: draft.inputs.identity.description,
        keywords: draft.inputs.identity.keywords,
        capabilities: draft.inputs.meta.capabilities,
        requiredInputs: draft.inputs.meta.required_inputs,
        renderer: draft.inputs.meta.renderer,
        suggestedActions: draft.inputs.meta.suggested_actions,
        department: draft.inputs.identity.department,
      },
      null,
      2,
    );

    const handlerName = `${dir.replace(/^studio_/, '').replace(/_/g, '')}_handler`;
    const handler = `"""${id} business logic — mock-rendered preview."""

from agent_db import AgentDB
from agent_runtime import ensure_session, check_permission_or_yield_error

AGENT_ID = "${id}"
DB = AgentDB(AGENT_ID)

def run_chat(*, session_id, message, audit_username, audit_role, user_email=""):
    # ... your business logic here ...
    yield {"type": "thinking"}
    yield {"type": "text", "content": "(scaffold preview — replace with real logic)"}
    yield {"type": "done"}
`;

    const prBody = `## Created via the Studio wizard

| Field | Value |
|---|---|
| **Action** | Create new agent |
| **Agent ID** | ${id} |
| **Submitter** | ${draft.submitted_by ?? draft.created_by} |
| **Approver** | ${draft.approver ?? '(TBD after admin approves)'} |
| **Wizard draft id** | ${draft.id} |

### Validation summary
- ✓ Identifier regex passed
- ✓ Lockstep check passed
- ✓ YAML / JSON parse OK

### Wizard inputs
\`\`\`json
${JSON.stringify(draft.inputs, null, 2)}
\`\`\`

---
*Opened by \`wg-aiagents-studio-publisher[bot]\` on behalf of the submitter.*
`;

    const pipelinesBlock = `${pascal}CodeBuildProject:
  Type: AWS::CodeBuild::Project
  Properties:
    Name: dev-wg-agents-studio-runtimes-${dir}
    Source:
      Type: CODEPIPELINE
      BuildSpec: singleshot/${dir}/buildspec.yaml
    ...

${pascal}Pipeline:
  Type: AWS::CodePipeline::Pipeline
  Properties:
    Name: dev-wg-agents-studio-runtimes-${dir}
    ...
    Triggers:
      - ProviderType: CodeStarSourceConnection
        GitConfiguration:
          Push:
            - Branches: { Includes: [dev] }
              FilePaths:
                Includes:
                  - singleshot/${dir}/**
                  - singleshot/deploy.py
                  - shared/**
                  - iam/**
`;

    return {
      [`singleshot/${dir}/agentcore.config.yaml`]: config,
      [`singleshot/${dir}/agent_meta.json`]: meta,
      [`singleshot/${dir}/${handlerName}.py`]: handler,
      [`iam/agent-execution-roles.yaml`]: iam,
      [`__PR_BODY__`]: prBody,
      [`__PIPELINES__`]: pipelinesBlock,
    };
  }

  // ── Approval workflow ───────────────────────────────────────────────

  // Accepts 'rejected' too: "request changes" sends the draft back to its
  // creator, who edits and resubmits. Resubmission clears the previous
  // decision fields (the rejection itself stays in the audit trail).
  // Accepts 'editing' too: a revision of a live agent submits as an update.
  submitDraft(id: string): Observable<Draft> {
    return this.transitionDraft(id, ['draft', 'editing', 'rejected'], 'pending_approval', (d) => {
      d.submitted_by = this.currentUser();
      d.submitted_at = this.now();
      d.submission_checks = this.computeSubmissionChecks(d);
      delete d.reviewer_comments;
      delete d.approver;
      delete d.decision_at;
      this.appendAudit(d, 'submitted');
    });
  }

  withdrawDraft(id: string): Observable<Draft> {
    return this.transitionDraft(id, 'pending_approval', 'draft', (d) => {
      delete d.submitted_at;
      this.appendAudit(d, 'withdrawn');
    });
  }

  listApprovals(): Observable<Draft[]> {
    if (this.currentRole().toLowerCase() !== 'admin') {
      return this.withDelay([] as Draft[]);
    }
    return this.withDelay(this.readDrafts().filter((d) => d.state === 'pending_approval'));
  }

  approveDraft(id: string, comment: string): Observable<Draft> {
    const me = this.currentUser();
    return defer(() => {
      // Four-eyes rule, mirroring the server (P1-G1 parity): the submitter
      // can never approve their own agent — the UI's disabled button is UX,
      // this is the actual gate.
      const draft = this.readDrafts().find((d) => d.id === id);
      if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      if ((draft.submitted_by ?? '') === me) {
        return throwError(
          () => new Error('You submitted this agent — a different admin must approve it.'),
        );
      }
      return this.transitionDraft(id, 'pending_approval', 'approved', (d) => {
        d.approver = me;
        d.decision_at = this.now();
        this.appendAudit(d, 'approved', comment);
      }).pipe(
        mergeMap((approved) => {
          // Cascade: approved -> publishing -> ... -> live (P1-G12 includes partial_merge).
          return this.publishCascade(approved);
        }),
      );
    });
  }

  rejectDraft(id: string, comment: string): Observable<Draft> {
    return this.transitionDraft(id, 'pending_approval', 'rejected', (d) => {
      d.approver = this.currentUser();
      d.decision_at = this.now();
      d.reviewer_comments = comment;
      this.appendAudit(d, 'rejected', comment);
    });
  }

  // ── Publishing ──────────────────────────────────────────────────────

  publishDraft(id: string): Observable<PublishResult> {
    return defer(() => {
      const draft = this.readDrafts().find((d) => d.id === id);
      if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      const pr1 = `https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/${1000 + Math.floor(Math.random() * 999)}`;
      const pr2 = `https://github.com/WG-AIAgents/wg-aiagents-pipelines/pull/${200 + Math.floor(Math.random() * 99)}`;
      // Start the cascade in the background.
      const updated = { ...draft, state: 'publishing' as DraftState, pr1_url: pr1, pr2_url: pr2 };
      this.upsertDraft(updated);
      this.publishCascade(updated).subscribe();
      return this.withDelay({ pr1_url: pr1, pr2_url: pr2, agent_id: draft.inputs.identity.agent_id });
    });
  }

  retryPublish(id: string): Observable<PublishResult> {
    const hints = readScenarioHints();
    return defer(() => {
      const draft = this.readDrafts().find((d) => d.id === id);
      if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      if (draft.state !== 'failed') {
        return throwError(() => new ConflictError('Draft is not in a failed state', draft.version, draft.state));
      }

      const pr1 = draft.pr1_url ?? `https://github.com/WG-AIAgents/wg-agents-studio-runtimes/pull/${1000 + Math.floor(Math.random() * 999)}`;
      const pr2 = draft.pr2_url ?? `https://github.com/WG-AIAgents/wg-aiagents-pipelines/pull/${200 + Math.floor(Math.random() * 99)}`;
      const updated: Draft = { ...draft, state: 'publishing', pr1_url: pr1, pr2_url: pr2 };
      this.upsertDraft(updated);

      // P1-G13: deterministic outcome via ?retry=succeed|fail.
      if (hints.retry === 'fail') {
        timer(2_000).subscribe(() => {
          const failed: Draft = {
            ...updated,
            state: 'failed',
            error_context: {
              failed_step: 'open_pr_pipelines_repo',
              error_message: 'GitHub returned 502 Bad Gateway (mock retry-fail)',
              completed_steps: ['pr1_opened'],
              occurred_at: this.now(),
            },
          };
          this.upsertDraft(failed);
        });
      } else {
        this.publishCascade(updated).subscribe();
      }

      return this.withDelay({ pr1_url: pr1, pr2_url: pr2, agent_id: draft.inputs.identity.agent_id });
    });
  }

  markResolvedManually(id: string): Observable<Draft> {
    return this.transitionDraft(id, 'failed', 'live', (d) => {
      delete d.error_context;
      this.appendAudit(d, 'manually_resolved');
      this.promoteToPublished(d);
    });
  }

  promoteToProd(id: string): Observable<Draft> {
    return this.transitionDraft(id, ['live', 'promoted'], 'promoted', (d) => {
      const envs = new Set([...(d.inputs.environments ?? ['dev']), 'prod']);
      d.inputs = { ...d.inputs, environments: ['dev', 'prod'].filter((e) => envs.has(e)) };
      this.appendAudit(d, 'promoted_to_prod');
    });
  }

  listPublished(): Observable<PublishedAgent[]> {
    return this.withDelay(this.readPublished());
  }

  /** Team directory — canned cross-creator rows so the page demos offline.
   *  One row is the current user's own (is_owner) so the "you" owner
   *  treatment shows alongside teammates' shared agents. */
  listDirectory(): Observable<DirectoryAgent[]> {
    const now = this.now();
    return this.withDelay<DirectoryAgent[]>([
      {
        agent_id: 'STUDIO_SALES_PIPELINE_AGENT',
        display_name: 'Sales Pipeline Agent',
        description: 'Answers questions about open opportunities and pipeline health from Salesforce.',
        department: 'Sales',
        state: 'live',
        owner: this.currentUser(),
        is_owner: true,
        published_at: now - 3 * 86_400,
      },
      {
        agent_id: 'STUDIO_SUPPORT_TRIAGE_AGENT',
        display_name: 'Support Triage Agent',
        description: 'Summarizes new support cases and suggests the right escalation queue.',
        department: 'Support',
        state: 'promoted',
        owner: 'jordan.lee@watchguard.com',
        is_owner: false,
        published_at: now - 21 * 86_400,
      },
      {
        agent_id: 'STUDIO_ENG_RELEASE_NOTES_AGENT',
        display_name: 'Release Notes Agent',
        description: 'Drafts customer-facing release notes from merged Jira tickets each sprint.',
        department: 'Engineering',
        state: 'live',
        owner: 'sam.varma@watchguard.com',
        is_owner: false,
        published_at: now - 86_400,
      },
    ]);
  }

  getPublishedDraft(agentId: string): Observable<Draft> {
    const draft = this.readDrafts().find((d) => d.inputs.identity.agent_id === agentId);
    if (!draft) return throwError(() => new NotFoundError(`Published agent ${agentId} not found`));
    return this.withDelay(draft);
  }

  /** W5 parity: the server advances pr_open → building → live on this call;
   *  the mock's publishCascade already advances on its own timers, so this
   *  just returns the current draft. */
  refreshStatus(id: string): Observable<Draft> {
    const draft = this.readDrafts().find((d) => d.id === id);
    if (!draft) return throwError(() => new NotFoundError(`Draft ${id} not found`));
    return this.withDelay(draft, 100);
  }

  editPublished(agentId: string): Observable<Draft> {
    const original = this.readDrafts().find((d) => d.inputs.identity.agent_id === agentId);
    if (!original) return throwError(() => new NotFoundError(`Published agent ${agentId} not found`));
    // Spawn a revision inheriting the live config. State 'editing' — the builder
    // surfaces the "editing the live agent → update (v2)" banner and a "Submit
    // update" action for this state; it still re-enters submit -> approve ->
    // publish. (The 'editing' state is handled throughout the UI; the real
    // studio-api should set it here too so the same treatment shows in prod.)
    const me = this.currentUser();
    const now = this.now();
    const newDraft: Draft = {
      id: uuid(),
      version: 1,
      state: 'editing',
      created_by: me,
      created_at: now,
      last_modified: now,
      inputs: structuredClone(original.inputs),
      audit_trail: [{ actor: me, action: 'edit_started', ts: now, comment: `Editing ${agentId}` }],
    };
    const all = this.readDrafts();
    all.push(newDraft);
    this.writeDrafts(all);
    return this.withDelay(newDraft);
  }

  deletePublished(agentId: string): Observable<void> {
    // Parity with the real service: decommissioning isn't automated yet.
    return throwError(() => new Error(
      `Decommissioning isn't automated yet — ask the platform team to remove `
      + `singleshot/${agentId.toLowerCase()} via PR.`,
    ));
  }

  /** Mirrors the backend rule engine closely enough to exercise every dialog
   *  state offline: `ghost*` is not a user, `viewer*` has no Studio role,
   *  `noabil*` lacks an ability, `unconnected*` hasn't connected a connector
   *  (automations), anything else is ready. */
  private readonly ownerHistory: OwnerHistoryEntry[] = [];

  transferOwner(ref: TransferRef, newOwner: string, dryRun = false): Observable<TransferOwnerResult> {
    const owner = (newOwner || '').trim().toLowerCase();
    const all = this.readDrafts();
    const anchor = ref.kind === 'draft'
      ? all.find((d) => d.id === ref.draftId)
      : all.find((d) => d.inputs.identity.agent_id === ref.agentId && ['live', 'promoted', 'failed', 'broken'].includes(d.state))
        ?? all.find((d) => d.inputs.identity.agent_id === ref.agentId);
    const agentId = ref.kind === 'published' ? ref.agentId : (anchor?.inputs.identity.agent_id ?? '');
    const current = (anchor?.created_by ?? this.currentUser()).toLowerCase();
    const kind = anchor?.inputs.shape.agent_kind === 'automation' ? 'automation' : 'chat';
    const base: TransferOwnerResult = {
      agent_id: agentId, display_name: anchor?.inputs.identity.display_name ?? agentId, kind,
      owner, previous_owner: current, revisions_transferred: 0, dry_run: dryRun,
    };
    if (!/^[a-z0-9._-]+$/.test(owner)) {
      return throwError(() => new Error(`'${owner}' is not an active user.`));
    }
    const checks: TransferCheck[] = [];
    const refuse = (c: TransferCheck) => {
      checks.push(c);
      return this.withDelay({ ...base, ok: false, checks, effects: [], follow_ups: [], message: c.message, code: 'transfer_blocked' });
    };
    if (owner === current) {
      return refuse({ id: 'target_differs', label: 'New owner', status: 'fail', message: `'${owner}' already owns this agent.` });
    }
    if (owner.startsWith('ghost')) {
      return refuse({ id: 'target_active', label: 'Active user', status: 'fail',
        message: `'${owner}' is not an active Agent Hub user. Check the username (first.last) and try again.` });
    }
    checks.push({ id: 'target_active', label: 'Active user', status: 'pass', message: `${owner} is an active user.` });
    if (owner.startsWith('viewer')) {
      checks.push({ id: 'target_studio_role', label: 'Studio access', status: 'fail',
        message: `'${owner}' cannot use Studio (role: user). Ask an administrator to grant them the developer role first.` });
    } else {
      checks.push({ id: 'target_studio_role', label: 'Studio access', status: 'pass', message: `${owner} can edit agents in Studio (developer).` });
    }
    if (owner.startsWith('noabil')) {
      checks.push({ id: 'target_abilities', label: 'Required abilities', status: 'fail',
        message: `${owner} doesn't have access to 2 abilities this agent uses: Search Jira issues, Search knowledge base. Ask an administrator to add them to a group that grants these, then transfer again.`,
        detail: { abilities: ['Search Jira issues', 'Search knowledge base'] } });
    } else {
      checks.push({ id: 'target_abilities', label: 'Required abilities', status: 'pass', message: `${owner} has every ability this agent uses.` });
    }
    checks.push(agentId
      ? { id: 'target_hub_access', label: 'Agent Hub access', status: 'info',
          message: `${owner} will be granted direct access to this agent as part of the transfer.`, detail: { grant_added: true } }
      : { id: 'target_hub_access', label: 'Agent Hub access', status: 'info', message: 'This agent has no id yet; Agent Hub access follows the first publish.' });
    if (kind === 'automation') {
      checks.push(owner.startsWith('unconnected')
        ? { id: 'target_connections', label: 'Connector connections', status: 'warn',
            message: `${owner} hasn't connected atlassian in Agent Hub. Scheduled runs use the owner's connection, so they will fail until the new owner connects.`,
            detail: { connectors: ['atlassian'], not_connected: ['atlassian'] } }
        : { id: 'target_connections', label: 'Connector connections', status: 'pass', message: `${owner} has connected atlassian.`, detail: { connectors: ['atlassian'] } });
    }
    const text = `${anchor?.inputs.prompt.role ?? ''} ${anchor?.inputs.prompt.instructions ?? ''}`.toLowerCase();
    if (current && text.includes(current.split('@')[0])) {
      checks.push({ id: 'owner_references', label: 'References to the current owner', status: 'warn',
        message: `The agent's configuration mentions ${current} (1 place: prompt.instructions). It will keep working as written — review these after the transfer so 'my'/'me' and recipients mean the new owner.`,
        detail: { references: [{ path: 'prompt.instructions', match: current }] } });
    }
    const blocked = checks.some((c) => c.status === 'fail');
    const lineage = anchor
      ? all.filter((d) => (d.lineage_id ?? d.id) === (anchor.lineage_id ?? anchor.id)
          || (!!agentId && d.inputs.identity.agent_id === agentId))
      : [];
    const live = !!anchor && ['live', 'promoted'].includes(anchor.state);
    const effects: TransferEffect[] = [
      { id: 'revisions', label: 'Studio ownership', status: 'will_move',
        message: `${lineage.length || 1} revision${(lineage.length || 1) === 1 ? '' : 's'} move to ${owner}'s My Agents; they gain edit rights and you lose them (unless you are an admin).` },
      { id: 'tenancy', label: 'Knowledge base & workspace', status: agentId ? 'will_move' : 'not_applicable',
        message: agentId ? 'Knowledge-base files and the agent workspace follow the new owner.' : 'This draft has no agent id yet; nothing to move.' },
      { id: 'catalog', label: 'Agent Hub catalog owner', status: live && kind === 'chat' ? 'will_move' : 'not_applicable',
        message: live && kind === 'chat' ? 'The Agent Hub catalog entry lists the new owner (Agent Hub can take up to a minute to reflect it).'
          : kind === 'automation' ? 'Scheduled automations have no Agent Hub catalog entry.' : 'Not deployed yet; the catalog owner follows the first deploy.' },
      { id: 'registry', label: 'Agent registry owner', status: live ? 'will_move' : 'not_applicable',
        message: live ? 'The platform agent registry (lifecycle, reports access) lists the new owner.' : 'Not deployed yet; the registry owner follows the first deploy.' },
      { id: 'access', label: 'Agent Hub access', status: agentId ? 'will_move' : 'not_applicable',
        message: agentId ? `${owner} is granted direct access to use this agent; existing group access is unchanged.` : 'No access change needed.' },
      ...(kind === 'automation' ? [{ id: 'automation_control', label: 'Run attribution', status: 'will_move' as const,
        message: 'Scheduled runs are attributed (and metered) to the new owner.' }] : []),
      { id: 'history', label: 'Ownership history', status: 'will_move', message: 'A permanent history record is written and both owners are e-mailed.' },
    ];
    const followUps: TransferEffect[] = kind === 'automation' && live ? [{
      id: 'rerender', label: 'Re-publish for the new owner', status: 'follow_up',
      message: "Scheduled runs use the OWNER's connector connections and failure alerts go to the owner's e-mail — both are baked into the deployed code. A re-publish is queued automatically after the transfer; until it goes live, runs still use " + current + "'s connections.",
    }] : [];
    const report: TransferOwnerResult = { ...base, ok: !blocked, checks, effects, follow_ups: followUps, revisions: lineage.length || 1 };
    if (blocked) {
      const failing = checks.find((c) => c.status === 'fail')!;
      return this.withDelay({ ...report, message: failing.message, code: failing.id === 'target_abilities' ? 'owner_missing_abilities' : 'transfer_blocked' });
    }
    if (dryRun) return this.withDelay(report);

    let n = 0;
    for (const d of all) {
      if (lineage.some((l) => l.id === d.id)) {
        d.created_by = owner;
        n += 1;
      }
    }
    this.writeDrafts(all);
    if (agentId) {
      const published = this.readPublished();
      for (const p of published) {
        if (p.agent_id === agentId) p.owner = owner;
      }
      this.writePublished(published);
    }
    this.ownerHistory.unshift({ from_owner: current, to_owner: owner, actor: this.currentUser(), actor_role: 'developer',
      source: 'studio', ts: Date.now(), revisions: n || 1, catalog_updated: live && kind === 'chat', access_moved: false });
    return this.withDelay({
      ...report, dry_run: false, revisions_transferred: n || 1,
      catalog_updated: live && kind === 'chat', registry_updated: live, access_moved: false,
      rerender: followUps.length ? { queued: true, message: `Re-publish queued: once it goes live, scheduled runs use ${owner}'s connections and alerts reach them.`, draft_id: 'mock-rev' } : null,
      notified: [`${owner}@watchguard.com`, `${current}@watchguard.com`],
      note: live ? '' : "This agent isn't deployed yet; its catalog and registry owner follow the first deploy.",
    });
  }

  getOwnerHistory(agentId: string): Observable<OwnerHistory> {
    const d = this.readDrafts().find((x) => x.inputs.identity.agent_id === agentId);
    return this.withDelay({ agent_id: agentId, owner: d?.created_by ?? this.currentUser(), history: [...this.ownerHistory] });
  }

  // ── Gateway tool catalog (mock canned catalog) ─────────────────────
  // A representative subset of the real published targets so the Abilities
  // catalog (search, categories, one-connector rule) demos realistically.

  listTools(): Observable<GatewayTarget[]> {
    return this.listToolCatalog().pipe(map((catalog) => catalog.targets));
  }

  listToolCatalog(): Observable<ToolCatalogResult> {
    const soql = (what: string) => ({
      type: 'object' as const,
      properties: { soql: { type: 'string', description: `A valid SOQL query for ${what}.` } },
      required: ['soql'],
    });
    const targets: GatewayTarget[] = [
      {
        target: 'atlassian-read',
        tools: [
          {
            name: 'atlassian-read___search_jira_issues',
            operationId: 'search_jira_issues',
            displayName: 'Search Jira Issues',
            description: 'Search Jira issues using JQL.',
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: { jql: { type: 'string', description: 'A valid JQL query string.' } },
              required: ['jql'],
            },
          },
          {
            name: 'atlassian-read___search_confluence',
            operationId: 'search_confluence',
            displayName: 'Search Confluence',
            description: 'Search Confluence content using CQL.',
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: { cql: { type: 'string', description: 'A valid CQL query string.' } },
              required: ['cql'],
            },
          },
        ],
      },
      {
        target: 'salesforce-read',
        tools: [
          {
            name: 'salesforce-read___query_accounts',
            operationId: 'query_accounts',
            displayName: 'Query Accounts',
            description: 'Query Salesforce accounts by SOQL.',
            sensitivity: 'internal',
            input_schema: soql('Accounts'),
          },
          {
            name: 'salesforce-read___query_opportunities',
            operationId: 'query_opportunities',
            displayName: 'Query Opportunities',
            description: 'Query Salesforce opportunities by SOQL.',
            sensitivity: 'internal',
            input_schema: soql('Opportunities'),
          },
          {
            name: 'salesforce-read___query_cases',
            operationId: 'query_cases',
            displayName: 'Query Cases',
            description: 'Query Salesforce support cases by SOQL.',
            sensitivity: 'internal',
            input_schema: soql('Cases'),
          },
        ],
      },
      {
        target: 'snowflake-read',
        tools: [
          {
            name: 'snowflake-read___query_partner_revenue',
            operationId: 'query_partner_revenue',
            displayName: 'Query Partner Revenue',
            description: 'Return partner POS/revenue rows from Snowflake.',
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: {
                group_by: { type: 'string', description: 'Dimension to group revenue by.' },
                top_n: { type: 'integer', description: 'How many rows to return.' },
              },
              required: ['group_by'],
            },
          },
          {
            name: 'snowflake-read___list_wins',
            operationId: 'list_wins',
            displayName: 'List Wins',
            description: 'List closed deals filtered by reason, product or region.',
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: {
                outcome: { type: 'string', description: 'wins, losses or both.' },
                filter: { type: 'string', description: 'Optional keyword filter.' },
              },
              required: ['outcome'],
            },
          },
        ],
      },
      {
        target: 'chilipiper-read',
        tools: [
          {
            name: 'chilipiper-read___cp_get_availability',
            operationId: 'cp_get_availability',
            displayName: 'Get Availability',
            description: 'Fetch available booking slots for a Chili Piper user.',
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: {
                email: { type: 'string', description: 'Email of the exec to check.' },
                window_days: { type: 'integer', description: 'How many days ahead to look.' },
              },
              required: ['email'],
            },
          },
        ],
      },
      {
        target: 'chilipiper-write',
        tools: [
          {
            name: 'chilipiper-write___cp_book_meeting',
            operationId: 'cp_book_meeting',
            displayName: 'Book Meeting',
            description: 'Book a Chili Piper meeting — sends calendar invites and updates CRM.',
            // Write sensitivity + a published friendly block, mirroring what
            // the gateway pipeline publishes — exercises the "Takes real
            // actions" warning and the catalog-over-local metadata priority.
            sensitivity: 'write',
            friendly: {
              name: 'Book a meeting',
              description: 'Books the meeting on the calendar and sends invites.',
              example: 'Book the 3pm slot on Tuesday.',
              badge: 'Books meetings',
              category: 'Communication',
              icon: 'clock',
              order: 1,
            },
            input_schema: {
              type: 'object',
              properties: {
                route_id: { type: 'string', description: 'Routing session id.' },
                start_time: { type: 'string', description: 'ISO-8601 slot start time.' },
                guest_email: { type: 'string', description: 'Primary guest email.' },
              },
              required: ['route_id', 'start_time', 'guest_email'],
            },
          },
        ],
      },
      {
        // The knowledge-base creator grant: presence of this target is how
        // the UI knows the caller may equip the __knowledge_base builtin
        // ("Answer from your documents"). It never renders as its own picker
        // card (equippableCatalog filters it) — the mock user is "granted"
        // so the KB flow demos offline end-to-end.
        target: 'knowledge-read',
        tools: [
          {
            name: 'knowledge-read___search_knowledge_base',
            operationId: 'search_knowledge_base',
            displayName: 'Search knowledge base',
            description: "Search the agent's uploaded knowledge-base documents and return the most relevant passages with citations.",
            sensitivity: 'internal',
            input_schema: {
              type: 'object',
              properties: {
                query: { type: 'string', description: 'What to look for in the documents.' },
              },
              required: ['query'],
            },
          },
        ],
      },
    ];
    // Presentation mirrors the server-owned arrangement contract the real
    // /tools now sends (same document the admin portal edits); the bundled
    // ABILITY_CATEGORIES doubles as the mock's copy of it.
    return this.withDelay<ToolCatalogResult>({
      targets,
      presentation: {
        category_order: [...ABILITY_CATEGORIES],
        default_category: 'Knowledge',
        sort_within_category: ['order', 'name'],
      },
      // Mock builder = full access (dev tool), so the KB builtin is available.
      knowledgeBaseGranted: true,
    });
  }

  /** Server-owned report starter presets — the mock serves the bundled copy,
   *  mirroring what the real GET /report-templates publishes. */
  listReportTemplates(): Observable<ReportTemplatePreset[]> {
    return this.withDelay<ReportTemplatePreset[]>(structuredClone(REPORT_TEMPLATE_PRESETS));
  }

  listGroups(): Observable<GroupOption[]> {
    return this.withDelay<GroupOption[]>([
      { group_id: 'sales', name: 'Sales', description: 'Sales org' },
      { group_id: 'support', name: 'Support', description: 'Customer support' },
      { group_id: 'engineering', name: 'Engineering', description: 'R&D' },
    ]);
  }

  // ── Abilities library (roadmap + requests) ──────────────────────────

  listComingSoon(): Observable<ComingSoonAbility[]> {
    return this.withDelay<ComingSoonAbility[]>(MOCK_COMING_SOON.map((a) => ({ ...a })));
  }

  requestAbility(req: AbilityRequest): Observable<{ status: string }> {
    try {
      const all = JSON.parse(localStorage.getItem(STORAGE_ABILITY_REQUESTS) || '[]') as unknown[];
      all.push({ ...req, requested_by: this.currentUser(), created_at: this.now() });
      localStorage.setItem(STORAGE_ABILITY_REQUESTS, JSON.stringify(all));
    } catch {
      /* demo-only persistence; ignore quota/parse errors */
    }
    return this.withDelay({ status: 'recorded' });
  }

  // ── Knowledge base (draft-scoped RAG documents, localStorage-backed) ──
  // Mirrors the real lifecycle: upload emits the confirmed doc as 'queued',
  // then a cascade advances queued → ingesting → ready over ~6s so the
  // wizard's polling/status chips demo offline. A filename containing "fail"
  // scripts the failure path, "partial" the partly-indexed path — the same
  // convention codegen/test-run use.

  private knowledgeKey(draftId: string): string {
    return `${STORAGE_KNOWLEDGE_PREFIX}${draftId}`;
  }

  private readKnowledgeDocs(draftId: string): KnowledgeDocument[] {
    try {
      return JSON.parse(localStorage.getItem(this.knowledgeKey(draftId)) || '[]') as KnowledgeDocument[];
    } catch {
      return [];
    }
  }

  private writeKnowledgeDocs(draftId: string, docs: KnowledgeDocument[]): void {
    try {
      localStorage.setItem(this.knowledgeKey(draftId), JSON.stringify(docs));
    } catch {
      /* demo-only persistence; ignore quota errors */
    }
  }

  private knowledgeQuota(docs: KnowledgeDocument[]): KnowledgeQuota {
    return {
      max_docs: MOCK_KNOWLEDGE_MAX_DOCS,
      max_total_bytes: MOCK_KNOWLEDGE_MAX_TOTAL_BYTES,
      max_file_bytes: MOCK_KNOWLEDGE_MAX_FILE_BYTES,
      used_docs: docs.length,
      used_bytes: docs.reduce((sum, d) => sum + (d.size_bytes || 0), 0),
    };
  }

  listKnowledgeDocs(draftId: string): Observable<KnowledgeDocumentList> {
    return defer(() => {
      const docs = this.readKnowledgeDocs(draftId);
      return this.withDelay<KnowledgeDocumentList>(
        { documents: docs, quota: this.knowledgeQuota(docs) },
        150, // the wizard polls every 4s — keep the mock snappy
      );
    });
  }

  uploadKnowledgeFile(
    draftId: string,
    file: File,
    onProgress?: (fraction: number) => void,
  ): Observable<KnowledgeDocument> {
    return defer(() => {
      const draft = this.readDrafts().find((d) => d.id === draftId);
      if (!draft) return throwError(() => new NotFoundError(`Draft ${draftId} not found`));

      const docs = this.readKnowledgeDocs(draftId);
      const quota = this.knowledgeQuota(docs);
      if (quota.used_docs >= quota.max_docs) {
        return throwError(() => new Error(
          `This agent already has ${quota.max_docs} documents — remove one before adding more.`,
        ));
      }
      if (quota.used_bytes + file.size > quota.max_total_bytes) {
        return throwError(() => new Error(
          'Adding this file would exceed the knowledge base storage limit — remove a document first.',
        ));
      }

      // Simulated browser→S3 byte progress while the "PUT" is in flight.
      [0.18, 0.42, 0.67, 0.88, 1].forEach((f, i) =>
        setTimeout(() => onProgress?.(f), 160 * (i + 1)),
      );

      const doc: KnowledgeDocument = {
        doc_id: uuid(),
        filename: file.name,
        title: file.name.replace(/\.[^.]+$/, ''),
        size_bytes: file.size,
        status: 'queued',
        uploaded_at: Math.floor(Date.now() / 1000),
        uploaded_by: this.currentUser(),
      };
      // Confirm lands after the simulated PUT: persist as 'queued', then let
      // the cascade advance it like the real ingestion worker would.
      return timer(950).pipe(
        map(() => {
          const current = this.readKnowledgeDocs(draftId);
          current.push(doc);
          this.writeKnowledgeDocs(draftId, current);
          this.knowledgeCascade(draftId, doc.doc_id, file.name);
          return { ...doc };
        }),
      );
    });
  }

  /** queued → ingesting → ready|partial|failed via setTimeout (codegenCascade twin). */
  private knowledgeCascade(draftId: string, docId: string, filename: string): void {
    const patch = (status: KnowledgeDocStatus, detail?: string): void => {
      const docs = this.readKnowledgeDocs(draftId);
      const idx = docs.findIndex((d) => d.doc_id === docId);
      if (idx === -1) return; // deleted mid-flight — never resurrect
      docs[idx] = { ...docs[idx], status, ...(detail ? { status_detail: detail } : {}) };
      this.writeKnowledgeDocs(draftId, docs);
    };

    const lower = filename.toLowerCase();
    setTimeout(() => patch('ingesting'), 2_000);
    setTimeout(() => {
      if (lower.includes('fail')) {
        patch('failed', 'The document could not be parsed (mock failure).');
      } else if (lower.includes('partial')) {
        patch('partial', 'Some pages could not be indexed (mock).');
      } else {
        patch('ready');
      }
    }, 6_000);
  }

  deleteKnowledgeDoc(draftId: string, docId: string): Observable<void> {
    return defer(() => {
      const docs = this.readKnowledgeDocs(draftId);
      if (!docs.some((d) => d.doc_id === docId)) {
        return throwError(() => new NotFoundError(`Document ${docId} not found`));
      }
      this.writeKnowledgeDocs(draftId, docs.filter((d) => d.doc_id !== docId));
      return this.withDelay(undefined as void, 200);
    });
  }

  // ── AI-assist (real Bedrock via Phase 1.14; falls back to canned) ────

  aiAssistPrompt(id: string, description: string, equippedAbilities?: string[]): Observable<WizardPrompt> {
    const hints = readScenarioHints();
    const delayMs = hints.ai === 'slow' ? DELAY_AI_SLOW_MS : DELAY_AI_FAST_MS;
    // Abilities-first: the user already chose the tools, so the canned
    // fallback echoes them (the real route grounds generation on them too)
    // rather than the description-keyword guess.
    const canned = this.cannedPrompt(description);
    const fallback: WizardPrompt = equippedAbilities?.length
      ? { ...canned, abilities: [...equippedAbilities] }
      : canned;
    return this.callBedrockOrFallback<WizardPrompt>(
      id,
      'prompt',
      { user_description: description, ...(equippedAbilities?.length ? { equipped_abilities: equippedAbilities } : {}) },
      delayMs,
      fallback,
    );
  }

  aiAssistTool(id: string, intent: string): Observable<AnthropicToolSchema> {
    const hints = readScenarioHints();
    const delayMs = hints.ai === 'slow' ? DELAY_AI_SLOW_MS : DELAY_AI_FAST_MS;
    return this.callBedrockOrFallback<AnthropicToolSchema>(
      id,
      'tool',
      { tool_intent: intent },
      delayMs,
      this.cannedTool(intent),
    );
  }

  aiAssistReportTemplate(
    id: string,
    description: string,
    format: 'html' | 'pdf' | 'xlsx' | 'pptx' = 'html',
  ): Observable<ReportTemplateResult> {
    const hints = readScenarioHints();
    const delayMs = hints.ai === 'slow' ? DELAY_AI_SLOW_MS : DELAY_AI_FAST_MS;
    // Deterministic fallback: parse the description into sections + a Markdown
    // skeleton (the real route also returns AI-generated CSS; mock keeps the
    // built-in theme, i.e. empty css).
    const derived = deriveReportFromDescription(description, { format });
    const fallback: ReportTemplateResult = {
      title: derived.title,
      sections: derived.sections,
      format: derived.format,
      markdown: reportSkeletonMarkdown({ title: derived.title, sections: derived.sections }),
      css: '',
    };
    return this.callBedrockOrFallback<ReportTemplateResult>(
      id,
      'report-template',
      { description, format },
      delayMs,
      fallback,
    );
  }

  aiAssistEmailTemplate(id: string, description: string): Observable<EmailTemplateResult> {
    const hints = readScenarioHints();
    const delayMs = hints.ai === 'slow' ? DELAY_AI_SLOW_MS : DELAY_AI_FAST_MS;
    // Deterministic fallback: a default themed body wrapping the report, with a
    // subject echoing the description (the real route returns AI-generated
    // subject/body/CSS; mock keeps the built-in theme, i.e. empty css).
    const fallback: EmailTemplateResult = {
      subject: (description || '').trim().slice(0, 80) || 'Your scheduled report',
      markdown: defaultEmailBodyMarkdown('s3+email'),
      css: '',
    };
    return this.callBedrockOrFallback<EmailTemplateResult>(
      id,
      'email-template',
      { description },
      delayMs,
      fallback,
    );
  }

  /** Deterministic local co-builder: predictable for demos and e2e, and a
   *  living spec of the patch contract the real LLM endpoint honors
   *  (sections pre-merged, tools resolved against the catalog). */
  coBuilder(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    document?: CoBuilderDocument,
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderResult> {
    void id;
    void history;
    void readiness; // guided-build questions are an LLM behaviour — no local analog
    void testOutput; // demo-critique iteration is an LLM behaviour — no local analog
    return timer(DELAY_AI_FAST_MS).pipe(
      mergeMap(() => this.listToolCatalog()),
      map((catalog) =>
        coBuilderLocalParse(inputs, message, catalog.targets, document, catalog.knowledgeBaseGranted),
      ),
    );
  }

  /** Chat persistence — in-memory per session (mirrors the server's
   *  per-draft transcript store; enough for demos and e2e). */
  private readonly coBuilderChats = new Map<string, CoBuilderChatMessage[]>();

  getCoBuilderChat(id: string): Observable<CoBuilderChatMessage[]> {
    return of(structuredClone(this.coBuilderChats.get(id) ?? []));
  }

  saveCoBuilderChat(id: string, messages: CoBuilderChatMessage[]): Observable<CoBuilderChatMessage[]> {
    const saved = structuredClone(messages).slice(-40);
    this.coBuilderChats.set(id, saved);
    return of(structuredClone(saved));
  }

  /** Streaming variant collapses to one 'done' frame — the local parser is
   *  instant, so there is nothing to stream. */
  coBuilderStream(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderStreamEvent> {
    void readiness;
    void testOutput;
    return this.coBuilder(id, message, inputs, history).pipe(
      map((result) => ({ status: 'done' as const, text: result.reply ?? '', result })),
    );
  }

  private callBedrockOrFallback<T>(
    draftId: string,
    suffix: 'prompt' | 'tool' | 'report-template' | 'email-template',
    body: Record<string, unknown>,
    delayMs: number,
    fallback: T,
  ): Observable<T> {
    // Lambda route is /agents/drafts/{id}/ai-assist/{suffix}; API Gateway
    // base-path mapping strips the leading /wgagentsstudio.
    const url = `${environment.BASE_URL}${environment.STUDIO_API.AGENTS_BASE}/drafts/${draftId}/ai-assist/${suffix}`;
    return timer(delayMs).pipe(
      mergeMap(() =>
        this.http.post<T>(url, body).pipe(
          catchError(() => of(fallback)),
        ),
      ),
    );
  }

  private cannedPrompt(description: string): WizardPrompt {
    const lower = description.toLowerCase();
    if (lower.includes('sale')) return CANNED_AI_PROMPTS['sales'];
    if (lower.includes('sku') || lower.includes('product')) return CANNED_AI_PROMPTS['sku'];
    if (lower.includes('jira') || lower.includes('confluence')) return CANNED_AI_PROMPTS['atlassian'];
    return CANNED_AI_PROMPTS['default'];
  }

  private cannedTool(intent: string): AnthropicToolSchema {
    return {
      name: intent.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40) || 'search_things',
      description: `Mock tool generated from intent: "${intent}"`,
      input_schema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'The search query.' },
        },
        required: ['query'],
      },
    };
  }

  // ── Smoke test ─────────────────────────────────────────────────────

  smokeTest(agentId: string, message: string): Observable<SmokeTestResult> {
    const hints = readScenarioHints();
    if (hints.smoke === 'timeout') {
      return timer(30_000).pipe(map(() => ({
        response: '',
        took_ms: 30_000,
        tool_calls: [],
        error: 'Agent took too long to respond',
      })));
    }
    if (hints.smoke === 'tool-fail') {
      return this.withDelay<SmokeTestResult>({
        response: '',
        took_ms: 1_200,
        tool_calls: [
          { name: 'search_things', input: { query: 'mock' }, error: 'Gateway returned 502 Bad Gateway' },
        ],
        error: 'Tool call failed',
      }, 1_200);
    }
    return this.withDelay<SmokeTestResult>({
      response: `Hello! I'm ${agentId}. You said: "${message}". I help with the task described in my system prompt. This is a mocked response from MOCK MODE.`,
      took_ms: 600,
      tool_calls: [],
    }, 600);
  }

  /** Live draft test-chat — mocked: answers from the draft config (no model).
   *  Mirrors the real endpoint's shape so the test panel works identically. */
  testChatDraft(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<DraftTestChatResult> {
    return defer(() => {
      const draft = this.readDrafts().find((d) => d.id === id);
      const cfg = inputs ?? draft?.inputs;
      if (!cfg) return throwError(() => new NotFoundError(`Draft ${id} not found`));

      const name = cfg.identity.display_name || 'Your agent';
      const tool = cfg.shape.has_tools ? pickToolForMessage(cfg.tools.filter((t) => t.name), message) : undefined;
      const tool_calls = tool
        ? [{ name: tool.name, input: { query: message.slice(0, 60) }, output: { mock: true } }]
        : [];
      const response = tool
        ? `MOCK live turn from **${name}** — in real mode I would call \`${tool.name}\` through the gateway and answer from its output.`
        : `MOCK live turn from **${name}** — answering from the draft prompt only (no tools enabled).`;

      return this.withDelay<DraftTestChatResult>({
        response,
        took_ms: 800,
        tool_calls,
        history: [...history, { role: 'user', content: message }, { role: 'assistant', content: response }],
      }, 800);
    });
  }

  /** Async variant collapses to one 'done' frame — the local mock is instant. */
  testChatDraftStream(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<TestChatStreamEvent> {
    return this.testChatDraft(id, message, history, inputs).pipe(
      map((result) => ({ status: 'done' as const, text: result.response ?? '', result })),
    );
  }

  /** Complex agents: simulated async codegen run. Mirrors the real lifecycle
   *  (202 queued → running → succeeded/failed written onto the draft, polled
   *  via getDraft). Feedback containing the word "fail" scripts the failure
   *  path so the error UI can be exercised in mock mode. */
  generateCode(id: string, feedback?: string): Observable<DraftCodegen> {
    return defer(() => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      const draft = all[idx];
      if (!draft.inputs.shape.needs_custom_code) {
        return throwError(() => new Error('This draft does not use custom code.'));
      }

      const prev = draft.codegen;
      if (prev && (prev.status === 'queued' || prev.status === 'running')) {
        return this.withDelay(prev, 100); // double-click guard, like the server
      }

      const me = this.currentUser();
      const fb = (feedback ?? '').trim();
      const history = [...(prev?.feedback_history ?? [])];
      if (fb) history.push({ feedback: fb, by: me, ts: this.now() });

      const queued: DraftCodegen = {
        status: 'queued',
        run_id: crypto.randomUUID(),
        requested_by: me,
        requested_at: this.now(),
        feedback: fb,
        feedback_history: history.slice(-10),
        // Previous files stay visible while the new run is in flight.
        ...(prev?.files ? { files: prev.files, extra_requirements: prev.extra_requirements } : {}),
      };
      all[idx] = { ...draft, codegen: queued };
      this.writeDrafts(all);
      this.codegenCascade(id, queued.run_id, fb);
      return this.withDelay(queued, 100);
    });
  }

  /** queued → running → succeeded|failed via setTimeout (mirrors publishCascade). */
  private codegenCascade(id: string, runId: string, feedback: string): void {
    const patch = (mutate: (cg: DraftCodegen, draft: Draft) => DraftCodegen) => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) return;
      const cg = all[idx].codegen;
      if (!cg || cg.run_id !== runId) return; // superseded — never clobber a newer run
      all[idx] = { ...all[idx], codegen: mutate({ ...cg }, all[idx]) };
      this.writeDrafts(all);
    };

    setTimeout(() => patch((cg) => ({ ...cg, status: 'running' })), 1_200);
    setTimeout(() => patch((cg, draft) => {
      if (feedback.toLowerCase().includes('fail')) {
        return {
          ...cg,
          status: 'failed',
          completed_at: this.now(),
          error: 'Generated code did not pass validation (mock failure).',
          error_details: ['scoring.py: Python syntax error at line 12 (mock)'],
        };
      }
      const agentId = draft.inputs.identity.agent_id || 'STUDIO_AGENT';
      const pkg = agentId.replace(/^STUDIO_/, '').toLowerCase() || 'agent';
      const logic = draft.inputs.custom_code?.logic_description || 'custom logic';
      return {
        ...cg,
        status: 'succeeded',
        completed_at: this.now(),
        spec_hash: `mock-${runId.slice(0, 8)}`,
        stale: false,
        files: {
          [`${pkg}_handler.py`]:
            `"""${agentId} handler — MOCK generated code.\n\nImplements: ${logic.slice(0, 120)}\n"""\n\n` +
            `def run_chat(*, session_id, message, audit_username, audit_role, user_email=""):\n` +
            `    yield {"type": "thinking"}\n` +
            `    yield {"type": "text", "content": "(mock generated agent reply)"}\n` +
            `    yield {"type": "done"}\n\n\n` +
            `def clear_session(session_id, audit_username, audit_role):\n` +
            `    yield {"type": "cleared"}\n`,
          [`src/${pkg}/custom_logic.py`]:
            `"""MOCK generated custom logic module.\n\n${feedback ? 'Applied feedback: ' + feedback + '\\n' : ''}"""\n\n` +
            `def apply_custom_logic(data):\n    return {"result": "mock", "input_size": len(str(data))}\n`,
        },
        extra_requirements: [],
        notes: 'Mock generation — real mode calls the STUDIO_CODEGEN runtime.',
        self_check: { attempts: 1, ast_ok: true },
        review: {
          verdict: 'pass',
          summary: 'Mock review: structure matches the handler contract; no security findings.',
          findings: [{
            severity: 'info', file: `src/${pkg}/custom_logic.py`, line: 4,
            message: 'Consider a docstring describing the return shape (mock finding).',
          }],
        },
      } as DraftCodegen;
    }), 3_500);
  }

  // ── Automation: preview run (draft test-run) ───────────────────────
  // Mirrors the real lifecycle (202 queued → running → succeeded/failed on
  // the draft's test_run, polled via getDraft). A task containing the word
  // "fail" scripts the failure path, same convention as codegen.

  testRunDraft(id: string): Observable<DraftTestRun> {
    return defer(() => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      const draft = all[idx];
      if (draft.inputs.shape.agent_kind !== 'automation') {
        return throwError(() => new Error('Preview runs are for automation agents.'));
      }
      const task = (draft.inputs.prompt.task ?? '').trim();
      if (!task) {
        return throwError(() => new Error('Give the agent a task first — the preview runs exactly that task.'));
      }
      const prev = draft.test_run;
      if (prev && (prev.status === 'queued' || prev.status === 'running')) {
        return this.withDelay(prev, 100); // double-click guard, like the server
      }
      const queued: DraftTestRun = {
        status: 'queued',
        run_id: crypto.randomUUID(),
        requested_by: this.currentUser(),
        requested_at: this.now(),
      };
      all[idx] = { ...draft, test_run: queued };
      this.writeDrafts(all);
      this.testRunCascade(id, queued.run_id, task, draft.inputs);
      return this.withDelay(queued, 100);
    });
  }

  /** queued → running → succeeded|failed via setTimeout (codegenCascade twin). */
  private testRunCascade(id: string, runId: string, task: string, inputs: WizardInput): void {
    const patch = (mutate: (tr: DraftTestRun) => DraftTestRun) => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) return;
      const tr = all[idx].test_run;
      if (!tr || tr.run_id !== runId) return; // superseded — never clobber a newer run
      all[idx] = { ...all[idx], test_run: mutate({ ...tr }) };
      this.writeDrafts(all);
    };

    setTimeout(() => patch((tr) => ({ ...tr, status: 'running', started_at: this.now() })), 1_000);
    setTimeout(() => patch((tr) => {
      if (task.toLowerCase().includes('fail')) {
        return {
          ...tr, status: 'failed', completed_at: this.now(),
          error: 'The model produced no output for this task (mock failure).',
        };
      }
      const name = inputs.identity.display_name || 'Your automation';
      const tool = inputs.shape.has_tools ? inputs.tools.find((t) => t.name) : undefined;
      // Per-recipient automations with a FROZEN data call preview the REAL
      // emails: a few rendered bodies + the full audience incl. a skipped
      // person (studio-api _per_recipient_preview contract). Any other email
      // run reports the results lists it found instead (data_candidates) so
      // the builder can pick one with "Use this every run" (wave 1b).
      const delivery = inputs.shape.delivery;
      const emailKind = delivery?.kind === 'email' || delivery?.kind === 's3+email';
      const frozen = !!(delivery?.per_recipient?.data_source?.tool ?? '').trim();
      const perRecipient = delivery?.recipient_mode === 'per_recipient' && frozen
        ? this.mockPerRecipientPreview(name, delivery.per_recipient ?? {})
        : emailKind
          ? { data_candidates: [this.mockJiraCandidate()] }
          : {};
      return {
        ...tr,
        status: 'succeeded',
        completed_at: this.now(),
        duration_ms: 2_400,
        model_rounds: tool ? 2 : 1,
        tool_calls: tool
          ? [{ name: tool.name, input: { query: task.slice(0, 60) }, output: { mock: true } }]
          : [],
        ...perRecipient,
        report_text:
          `# ${name} — preview run\n\n` +
          `**Task:** ${task}\n\n` +
          `MOCK report — in real mode this is the exact report a scheduled run ` +
          `would deliver${tool ? `, built from \`${tool.name}\` results` : ''}.\n\n` +
          `- Finding one (mock)\n- Finding two (mock)\n\n_No delivery happens in preview._`,
      };
    }), 3_500);
  }

  /** The one results list a normal (unfrozen) mock run "found": a Jira search
   *  split by assignee — the studio-api data_candidates shape. */
  private mockJiraCandidate(): DataCandidate {
    return {
      index: 0,
      tool: 'searchJiraIssuesUsingJql',
      args: { jql: 'project = WAT AND statusCategory != Done', maxResults: 100 },
      rows_path: 'issues',
      rows: 47,
      person_field: { path: 'fields.assignee.emailAddress', kind: 'email', label: 'assignee' },
      person_fallback: { path: 'fields.assignee.displayName', kind: 'display_name' },
      directory_tool: 'search_employees',
      people: 11,
      unresolved: 1,
      label: 'Jira search · project = WAT AND statusCategory != Done · 47 issues · 11 people by assignee',
    };
  }

  private mockPerRecipientPreview(
    name: string,
    pr: WizardPerRecipient,
  ): Pick<DraftTestRun, 'email_previews' | 'recipients' | 'email_body'> {
    const owner = pr.audience?.source === 'owner';
    const people = owner
      ? [{ to: this.currentUser(), rows: 7 }]
      : [{ to: 'alex.rivera@watchguard.com', rows: 4 }, { to: 'priya.nair@watchguard.com', rows: 2 }];
    const body = (to: string, rows: number): string =>
      pr.render === 'template' && pr.template?.body
        ? pr.template.body.replace(/\[\[recipient\]\]/g, to).replace(/\[\[count\]\]/g, String(rows))
            .replace(/\[\[rows\]\]/g, rows ? '- MOCK-1 Example row\n- MOCK-2 Example row' : '(none)')
        : `Hi ${to.split('@')[0]},\n\nMOCK — ${name} found **${rows} item${rows === 1 ? '' : 's'}** for you this run.` +
          (pr.team_context ? `\n\nYou rank 1 of ${people.length} on the team (team total ${rows * people.length}).` : '') +
          `\n\n— ${name}`;
    const previews: EmailPreview[] = people.map((p) => ({ to: p.to, kind: 'individual', rows: p.rows, body: body(p.to, p.rows) }));
    if (!owner && pr.rollup?.enabled) {
      previews.push({
        to: 'manager@watchguard.com', kind: 'rollup', rows: people.reduce((n, p) => n + p.rows, 0),
        body: `MOCK rollup for your team:\n\n${people.map((p) => `- ${p.to}: ${p.rows} item(s)`).join('\n')}`,
      });
    }
    const recipients = previews.map((p) => ({ to: p.to, kind: p.kind, status: 'preview', rows: p.rows }));
    if (!owner) recipients.push({ to: 'J. Doe', kind: 'individual', status: 'skipped_unmatched', rows: 1 });
    return { email_previews: previews, recipients, email_body: previews[0]?.body };
  }

  // ── Automation: live operations (run history / Run now / kill switch) ──

  private runsKey(agentId: string): string {
    return `${STORAGE_RUNS_PREFIX}${agentId}`;
  }

  private readRuns(agentId: string): AgentRun[] {
    try {
      const raw = localStorage.getItem(this.runsKey(agentId));
      if (raw) return JSON.parse(raw) as AgentRun[];
    } catch { /* re-seed below */ }
    const now = this.now();
    const seeded: AgentRun[] = [
      {
        run_id: `${new Date((now - 3_600) * 1000).toISOString()}#mock3`, status: 'done',
        trace_id: '1-mock3aaaa-00000000000000000000mock3',
        triggered_by: 'schedule', started_at: now - 3_600, finished_at: now - 3_540,
        duration_ms: 58_000, channels: ['s3', 'email'], report_url: 'https://example.invalid/report/3',
      },
      {
        run_id: `${new Date((now - 90_000) * 1000).toISOString()}#mock2`, status: 'error',
        trace_id: '1-mock2bbbb-00000000000000000000mock2',
        triggered_by: 'schedule', started_at: now - 90_000, finished_at: now - 89_950,
        duration_ms: 41_000, error: 'Gateway tool timed out (mock).',
        state_detail: 'timed_out',
      },
      {
        run_id: `${new Date((now - 176_400) * 1000).toISOString()}#mock1`, status: 'done',
        trace_id: '1-mock1cccc-00000000000000000000mock1',
        triggered_by: 'schedule', started_at: now - 176_400, finished_at: now - 176_340,
        duration_ms: 63_000, channels: ['s3'], report_url: 'https://example.invalid/report/1',
        delivery_note: 'email skipped: no verified sender (mock)',
      },
    ];
    localStorage.setItem(this.runsKey(agentId), JSON.stringify(seeded));
    return seeded;
  }

  private writeRuns(agentId: string, runs: AgentRun[]): void {
    localStorage.setItem(this.runsKey(agentId), JSON.stringify(runs.slice(0, 50)));
  }

  listRuns(agentId: string): Observable<AgentRunsResult> {
    return defer(() => {
      const state = (localStorage.getItem(`${STORAGE_SCHEDULE_PREFIX}${agentId}`)
        ?? 'ENABLED') as ScheduleState;
      // The durable `paused` flag the agent honors — derived from the same
      // store the kill switch writes, so it stays in lockstep with the rule
      // state (the real server returns it independently from the runs row).
      return this.withDelay<AgentRunsResult>({
        runs: this.readRuns(agentId),
        schedule_state: state,
        paused: state === 'DISABLED',
      }, DELAY_FAST_MS);
    });
  }

  /** Per-user Run-now timestamps (ms) for the mock rate limiter — mirrors the
   *  real _run_now's 4/min/user throttle so the 429 toast path is exercised. */
  private readonly runNowCalls = new Map<string, number[]>();
  private static readonly RUN_NOW_LIMIT = 4;
  private static readonly RUN_NOW_WINDOW_MS = 60_000;

  runNow(agentId: string): Observable<{ accepted: boolean; message?: string }> {
    return defer(() => {
      // 409 — the trigger Lambda refuses unless the agent is live (real _run_now).
      const agent = this.readPublished().find((a) => a.agent_id === agentId);
      const draft = this.readDrafts().find((d) => d.inputs.identity.agent_id === agentId);
      const state = agent?.state ?? draft?.state;
      if (state && state !== 'live') {
        return throwError(() => new ConflictError(
          `This agent isn't live yet (current state: ${state}). Run-now is available once it's deployed.`,
          draft?.version ?? 0,
          state,
        ));
      }

      // 429 — per-user rate limit (4 runs/min), like the real server.
      const me = this.currentUser();
      const nowMs = Date.now();
      const recent = (this.runNowCalls.get(me) ?? []).filter(
        (t) => nowMs - t < MockAgentBuilderService.RUN_NOW_WINDOW_MS,
      );
      if (recent.length >= MockAgentBuilderService.RUN_NOW_LIMIT) {
        return throwError(() => new Error(
          'Too many manual runs — you can start at most 4 per minute. Wait a moment and try again.',
        ));
      }
      recent.push(nowMs);
      this.runNowCalls.set(me, recent);

      const now = this.now();
      const run: AgentRun = {
        run_id: `${new Date(now * 1000).toISOString()}#now`, status: 'started',
        trace_id: `1-${Math.floor(now).toString(16)}-${uuid().replace(/-/g, '').slice(0, 24)}`,
        triggered_by: `user:${me}`, started_at: now,
      };
      this.writeRuns(agentId, [run, ...this.readRuns(agentId)]);
      // The "run" completes a few seconds later, like the real trigger path.
      setTimeout(() => {
        const runs = this.readRuns(agentId);
        const idx = runs.findIndex((r) => r.run_id === run.run_id);
        if (idx === -1) return;
        runs[idx] = {
          ...runs[idx], status: 'done', finished_at: this.now(), duration_ms: 4_200,
          channels: ['s3'], report_url: 'https://example.invalid/report/now',
        };
        this.writeRuns(agentId, runs);
      }, 4_000);
      return this.withDelay({ accepted: true, message: 'Run started — it will appear in the run history.' }, DELAY_FAST_MS);
    });
  }

  setScheduleState(agentId: string, enabled: boolean): Observable<{ schedule_state: ScheduleState }> {
    return defer(() => {
      const state: ScheduleState = enabled ? 'ENABLED' : 'DISABLED';
      localStorage.setItem(`${STORAGE_SCHEDULE_PREFIX}${agentId}`, state);
      return this.withDelay({ schedule_state: state }, DELAY_FAST_MS);
    });
  }

  /** Re-presign a run's report. Mirrors GET .../runs/{run_id}/report-url:
   *  mints a FRESH (fake) presigned URL so the "Get fresh link" action works
   *  even when the run's stored report_url has expired. A run with no report
   *  (e.g. an errored run, or one that only delivered by email) 404s, same as
   *  the server. */
  refreshReportUrl(agentId: string, runId: string): Observable<{ url: string }> {
    return defer(() => {
      const run = this.readRuns(agentId).find((r) => r.run_id === runId);
      if (!run) return throwError(() => new NotFoundError(`Run ${runId} not found`));
      if (!run.report_url) {
        return throwError(() => new NotFoundError('This run has no saved report to link to.'));
      }
      // A fresh presign — new expiry query param so it's visibly a new URL.
      const fresh = mockFreshReportUrl(run.report_url, this.now());
      return this.withDelay({ url: fresh }, DELAY_FAST_MS);
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────

  private upsertDraft(draft: Draft): void {
    const all = this.readDrafts();
    const idx = all.findIndex((d) => d.id === draft.id);
    if (idx === -1) all.push(draft);
    else all[idx] = draft;
    this.writeDrafts(all);
  }

  private transitionDraft(
    id: string,
    expected: DraftState | DraftState[],
    next: DraftState,
    mutator: (draft: Draft) => void,
  ): Observable<Draft> {
    const expectedStates = Array.isArray(expected) ? expected : [expected];
    return defer(() => {
      const all = this.readDrafts();
      const idx = all.findIndex((d) => d.id === id);
      if (idx === -1) return throwError(() => new NotFoundError(`Draft ${id} not found`));
      const current = all[idx];
      if (!expectedStates.includes(current.state)) {
        return throwError(
          () => new ConflictError(
            `Expected state ${expectedStates.join(' or ')}, found ${current.state}`,
            current.version,
            current.state,
          ),
        );
      }
      const updated: Draft = {
        ...current,
        state: next,
        version: current.version + 1,
        last_modified: this.now(),
      };
      mutator(updated);
      all[idx] = updated;
      this.writeDrafts(all);
      return this.withDelay(updated);
    });
  }

  private appendAudit(draft: Draft, action: string, comment?: string): void {
    draft.audit_trail = draft.audit_trail ?? [];
    draft.audit_trail.push({ actor: this.currentUser(), action, ts: this.now(), comment });
    if (draft.audit_trail.length > 50) {
      draft.audit_trail = draft.audit_trail.slice(-50); // P1-G24 mirror
    }
  }

  /**
   * Publish cascade (P1-G12): approved → publishing → pr_open → partial_merge → building → deploying → live.
   * Each transition is a setTimeout; localStorage is updated so state survives navigation + cross-tab.
   */
  private publishCascade(draft: Draft): Observable<Draft> {
    const me = this.currentUser();
    const id = draft.id;
    const steps: Array<{ ts: number; state: DraftState }> = [
      { ts: 0,     state: 'publishing' },
      { ts: 2_000, state: 'pr_open' },
      { ts: 5_000, state: 'partial_merge' },
      { ts: 8_000, state: 'building' },
      { ts: 11_000, state: 'deploying' },
      { ts: 14_000, state: 'live' },
    ];

    steps.forEach((step) => {
      setTimeout(() => {
        const all = this.readDrafts();
        const idx = all.findIndex((d) => d.id === id);
        if (idx === -1) return;
        all[idx] = { ...all[idx], state: step.state, version: all[idx].version + 1, last_modified: this.now() };
        this.appendAudit(all[idx], `state:${step.state}`);
        this.writeDrafts(all);
        if (step.state === 'live') {
          this.promoteToPublished(all[idx]);
        }
      }, step.ts);
    });

    return this.withDelay(draft, 100);
  }

  private promoteToPublished(draft: Draft): void {
    const published = this.readPublished();
    const existing = published.findIndex((p) => p.agent_id === draft.inputs.identity.agent_id);
    const entry: PublishedAgent = {
      agent_id: draft.inputs.identity.agent_id,
      display_name: draft.inputs.identity.display_name,
      description: draft.inputs.identity.description,
      department: draft.inputs.identity.department,
      color: draft.inputs.identity.color,
      state: 'live',
      pr1_url: draft.pr1_url,
      pr2_url: draft.pr2_url,
      last_activity: this.now(),
      submitted_by: draft.submitted_by ?? draft.created_by,
      owner: draft.created_by,
      approver: draft.approver,
    };
    if (existing === -1) published.push(entry);
    else published[existing] = entry;
    this.writePublished(published);
  }
}
